import { useEffectPlease } from "@features/utils";
import {
  ArrowUturnLeftIcon,
  CursorArrowRaysIcon,
  MapPinIcon,
} from "@heroicons/react/16/solid";
import {
  CircleBackslashIcon,
  CircleIcon,
  EraserIcon,
  Half2Icon,
  Pencil1Icon,
  SquareIcon,
} from "@radix-ui/react-icons";
import { IconButton, Separator, Tooltip } from "@radix-ui/themes";
import { createContext, useCallback, useEffect, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { twMerge } from "tailwind-merge";
import { ChipMarker } from "./chip-markers";
import { DrawingCanvas, DrawingPath, useDrawingCanvas } from "./drawing-canvas";

type Context = {
  scale: number;
  snapping: boolean;
  pencil?: undefined | "eraser" | "brush" | "square" | "circle";
  chipMode?: boolean;
  readonly?: boolean;
  invert?: () => void;
  reset?: () => void;
};

export const CameraContext = createContext<{
  context: Context;
  setContext: (context: Partial<Context>) => void;
} | null>(null);

export const Frame = ({
  children,
  width = 500,
  height = 500,
  onUndo,
  onRedo,
  readonly,
  drawingMode,
  drawing,
  onCommitDrawing,
  refDrawingSVG,
  className,
  // Chip mode props
  chipMode,
  onChipModeChange,
}: {
  children: React.ReactNode;
  width?: number;
  height?: number;
  onUndo?: () => void;
  onRedo?: () => void;
  drawingMode?: "edit" | "erase" | "faces";
  readonly?: boolean;
  drawing?: DrawingPath[]; // Drawing paths representing the drawing mask
  onCommitDrawing?: (drawing: DrawingPath[]) => void; // When mouse up / touch end, send the new drawing paths
  refDrawingSVG?: (drawing_svg_ref: React.RefObject<SVGSVGElement>) => void; // Get the drawing canvas ref
  className?: string;
  // Chip mode props
  chipMode?: boolean;
  chips?: ChipMarker[];
  onChipModeChange?: (active: boolean) => void;
  onAddChip?: (chip: ChipMarker) => void;
  onRemoveChip?: (chipId: string) => void;
  onMoveChip?: (chipId: string, x: number, y: number) => void;
  selectedChipId?: string | null;
  onSelectChip?: (chipId: string | null) => void;
}) => {
  const [context, _setContext] = useState<Context>({
    scale: 1,
    snapping: true,
    readonly,
    chipMode: chipMode,
  });
  const setContext = useCallback(
    (newContext: Partial<Context>) => {
      _setContext((prev: Context) => ({ ...prev, ...newContext }));
    },
    [_setContext]
  );

  // Sync context.chipMode with prop when it changes
  useEffect(() => {
    setContext({ chipMode });
  }, [chipMode, setContext]);

  // Drawing canvas hook
  const { handleSVGChange, handleDrawingChange } = useDrawingCanvas();

  const [didInit, setDidInit] = useState(false);
  const resizeObserver = useRef<ResizeObserver | null>(null);
  const resizingDelayTimer = useRef<any>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const childrenRef = useRef<HTMLDivElement>(null);
  const widthRef = useRef(width);
  const heightRef = useRef(height);
  widthRef.current = width;
  heightRef.current = height;

  const updateFrameSize = useCallback(() => {
    const width = widthRef.current;
    const height = heightRef.current;

    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const isMobile = window.innerWidth < 768;

    const newWidth = rect.width;
    const newHeight = isMobile
      ? Math.min(window.innerHeight * 0.5, rect.width * 1.8)
      : rect.height;
    let scale = 1;

    const margin = 1;

    if (width > newWidth * margin || height > newHeight * margin) {
      // Then we must scale down the entire frame
      const scaleX = newWidth / width;
      const scaleY = newHeight / height;
      scale = Math.min(scaleX, scaleY) * margin;
    }

    setContext({ scale });

    const translateX = Math.round((newWidth - width * scale) / 2);
    const translateY = Math.round((newHeight - height * scale) / 2);

    // Ensure scale keep crisp rendering, knowning that the image is divisible by 8, allow 0.125 increments
    scale = Math.round(scale * 8) / 8;

    frameRef.current!.style.transform = `scale(${scale})`;

    frameRef.current!.style.marginLeft = `${translateX}px`;
    if (!isMobile) {
      frameRef.current!.style.marginTop = `${translateY}px`;
    } else {
      containerRef.current!.style.height = `${height * scale}px`;
      frameRef.current!.style.marginTop = "0px";
    }

    setTimeout(() => {
      setDidInit(true);
    }, 500);
  }, [containerRef, frameRef, setContext]);

  useEffectPlease(() => {
    // Initial size update
    updateFrameSize();
  }, [width, height]);

  useEffectPlease(() => {
    // Resize the frame to fit the container
    // Observe the container size changes
    resizeObserver.current = new ResizeObserver(() => {
      clearTimeout(resizingDelayTimer.current);
      resizingDelayTimer.current = setTimeout(() => {
        // check if the observed div is still mounted
        // else this will cause memory leak
        const isMobile = window.innerWidth < 768;
        if (containerRef.current && !isMobile) {
          updateFrameSize();
        }
      }, 100);
    });

    if (containerRef.current) {
      resizeObserver.current.observe(containerRef.current);
    }
    return () => {
      if (resizeObserver.current) resizeObserver.current.disconnect();
    };
  }, []);

  const svgRef = useRef<any>(null);

  const applyMask = useCallback(() => {
    if (childrenRef.current) {
      if (drawingMode === "erase" && svgRef.current) {
        // Create a black and white mask from the SVG
        childrenRef.current.style.maskImage = `url(data:image/svg+xml;base64,${btoa(
          new XMLSerializer()
            .serializeToString(svgRef.current)
            .replaceAll("white", "#000000")
            .replaceAll("black", "#ffffff")
        )})`;
        childrenRef.current.style.maskMode = "luminance";
      } else {
        childrenRef.current.style.maskImage = ``;
      }
    }
  }, [drawingMode]);

  useEffectPlease(() => {
    applyMask();
    if (drawingMode === "erase" || drawingMode === "edit") {
      setContext({ pencil: "brush" });
    } else {
      setContext({ pencil: undefined });
    }
  }, [drawingMode]);

  useHotkeys(
    ["ctrl+z", "cmd+z"],
    () => {
      console.log("Undo action triggered");
      onUndo?.();
    },
    { preventDefault: true, useKey: true }
  );
  useHotkeys(["ctrl+y", "cmd+y"], () => onRedo?.(), {
    preventDefault: true,
    useKey: true,
  });
  useHotkeys(
    ["ctrl+u", "cmd+u"],
    () => setContext({ snapping: !context.snapping }),
    {
      preventDefault: true,
      useKey: true,
    }
  );

  // Drawing tool hotkeys
  useHotkeys(
    ["ctrl+e", "cmd+e"],
    () =>
      setContext({
        pencil: context.pencil === "eraser" ? undefined : "eraser",
      }),
    {
      preventDefault: true,
      useKey: true,
    }
  );

  useHotkeys(
    ["ctrl+b", "cmd+b"],
    () =>
      setContext({ pencil: context.pencil === "brush" ? undefined : "brush" }),
    {
      preventDefault: true,
      useKey: true,
    }
  );

  useHotkeys(["ctrl+h", "cmd+h"], () => setContext({ pencil: undefined }), {
    preventDefault: true,
    useKey: true,
  });

  return (
    <CameraContext.Provider value={{ context, setContext }}>
      {!readonly && (
        <div className="z-50 absolute -top-4 md:top-0 md:left-0 flex items-center justify-between p-1 px-2 bg-slate-100 dark:bg-slate-900 rounded-lg w-full w-min space-x-2">
          <div className="flex items-center space-x-1 min-h-6 !-mr-1">
            <Tooltip content="Undo (Ctrl+Z)">
              <IconButton
                size="1"
                className="w-4"
                variant="ghost"
                color="gray"
                disabled={!onUndo}
                onClick={onUndo}
              >
                <ArrowUturnLeftIcon className="w-4 h-4 cursor-pointer" />
              </IconButton>
            </Tooltip>
            <Tooltip content="Redo (Ctrl+Y)">
              <IconButton
                size="1"
                className="w-4"
                variant="ghost"
                color="gray"
                disabled={!onRedo}
                onClick={onRedo}
              >
                <ArrowUturnLeftIcon className="w-4 h-4 cursor-pointer scale-x-[-1]" />
              </IconButton>
            </Tooltip>
            <Tooltip content="Toggle snapping (Ctrl+U)">
              <IconButton
                size="1"
                variant="ghost"
                color="gray"
                className={twMerge(
                  "w-4 m-0",
                  context.snapping ? "text-yellow-500" : "opacity-20"
                )}
                onClick={() => setContext({ snapping: !context.snapping })}
              >
                <CursorArrowRaysIcon className="w-4 h-4 cursor-pointer" />
              </IconButton>
            </Tooltip>
          </div>
          {(drawingMode === "edit" || drawingMode === "erase") && !chipMode && (
            <>
              <Separator orientation="vertical" />
              <div className="flex items-center space-x-1 min-h-6">
                <Tooltip content="Eraser (Ctrl+E)">
                  <IconButton
                    size="1"
                    color="gray"
                    variant="ghost"
                    className={twMerge(
                      "w-4 m-0",
                      context.pencil === "eraser"
                        ? "text-blue-500 bg-blue-500/20"
                        : ""
                    )}
                    onClick={() =>
                      setContext({
                        pencil:
                          context.pencil === "eraser" ? undefined : "eraser",
                      })
                    }
                  >
                    <EraserIcon className="w-4 h-4 cursor-pointer" />
                  </IconButton>
                </Tooltip>
                <Tooltip content="Editing pen (Ctrl+B)">
                  <IconButton
                    size="1"
                    color="gray"
                    variant="ghost"
                    className={twMerge(
                      "w-4 m-0",
                      context.pencil === "brush"
                        ? "text-blue-500 bg-blue-500/20"
                        : ""
                    )}
                    onClick={() =>
                      setContext({
                        pencil:
                          context.pencil === "brush" ? undefined : "brush",
                      })
                    }
                  >
                    <Pencil1Icon className="w-4 h-4 cursor-pointer" />
                  </IconButton>
                </Tooltip>
                <Tooltip content="Square pen">
                  <IconButton
                    size="1"
                    color="gray"
                    variant="ghost"
                    className={twMerge(
                      "w-4 m-0",
                      context.pencil === "square"
                        ? "text-blue-500 bg-blue-500/20"
                        : ""
                    )}
                    onClick={() =>
                      setContext({
                        pencil:
                          context.pencil === "square" ? undefined : "square",
                      })
                    }
                  >
                    <SquareIcon className="w-4 h-4 cursor-pointer" />
                  </IconButton>
                </Tooltip>
                <Tooltip content="Circle pen">
                  <IconButton
                    size="1"
                    color="gray"
                    variant="ghost"
                    className={twMerge(
                      "w-4 m-0",
                      context.pencil === "circle"
                        ? "text-blue-500 bg-blue-500/20"
                        : ""
                    )}
                    onClick={() =>
                      setContext({
                        pencil:
                          context.pencil === "circle" ? undefined : "circle",
                      })
                    }
                  >
                    <CircleIcon className="w-4 h-4 cursor-pointer" />
                  </IconButton>
                </Tooltip>
              </div>
              <Separator orientation="vertical" />
              <div className="flex items-center space-x-1 min-h-6">
                <Tooltip content="Invert selection">
                  <IconButton
                    size="1"
                    color="gray"
                    variant="ghost"
                    className={"w-4 m-0"}
                    onClick={() => {
                      context.invert && context.invert();
                    }}
                  >
                    <Half2Icon className="w-4 h-4 cursor-pointer" />
                  </IconButton>
                </Tooltip>
                <Tooltip content="Reset selection">
                  <IconButton
                    size="1"
                    color="gray"
                    variant="ghost"
                    className={"w-4 m-0"}
                    onClick={() => {
                      context.reset && context.reset();
                    }}
                  >
                    <CircleBackslashIcon className="w-4 h-4 cursor-pointer" />
                  </IconButton>
                </Tooltip>
              </div>
            </>
          )}
          {/* Chip marker toggle - always visible when in edit/erase mode */}
          {(drawingMode === "edit" || drawingMode === "erase") && (
            <>
              <Separator orientation="vertical" />
              <div className="flex items-center space-x-1 min-h-6">
                <Tooltip content="Point markers (click to place numbered markers)">
                  <IconButton
                    size="1"
                    color="gray"
                    variant="ghost"
                    className={twMerge(
                      "w-4 m-0",
                      chipMode ? "text-green-500 bg-green-500/20" : ""
                    )}
                    onClick={() => {
                      const newChipMode = !chipMode;
                      setContext({ chipMode: newChipMode });
                      onChipModeChange?.(newChipMode);
                    }}
                  >
                    <MapPinIcon className="w-4 h-4 cursor-pointer" />
                  </IconButton>
                </Tooltip>
              </div>
            </>
          )}
        </div>
      )}
      <div
        ref={containerRef}
        className={twMerge(
          "w-full h-full bg-white dark:bg-slate-950 cursor-default group/frame overflow-hidden",
          className
        )}
      >
        <div
          ref={frameRef}
          style={{ width, height, transformOrigin: "left top" }}
          className={twMerge(
            "relative ease-out bg-slate-500/20",
            didInit && "transition-all duration-300",
            readonly && "bg-transparent",
            context.pencil && "pointer-events-none"
          )}
        >
          {!readonly && (
            <div className="bg-checkerboard absolute inset-0 z-0 pointer-events-none w-full h-full" />
          )}
          <div ref={childrenRef}>
            <div className="w-full h-full">{children}</div>
          </div>
          {/* Drawing Canvas - hide when in chip mode */}
          {!readonly &&
            (drawingMode === "edit" || drawingMode === "erase") &&
            !chipMode && (
              <DrawingCanvas
                refDrawingSVG={refDrawingSVG}
                width={width}
                height={height}
                pencil={context.pencil}
                mode={drawingMode}
                drawing={drawing}
                onChange={(svg) => {
                  svgRef.current = svg;
                  applyMask();
                  handleSVGChange(svg);
                }}
                onCommit={(drawing) => {
                  handleDrawingChange(drawing);
                  onCommitDrawing?.(drawing);
                }}
              />
            )}

          {!readonly && (
            <div className="opacity-100 group-hover/frame:opacity-50 transition-opacity duration-300">
              <div className="absolute z-10 top-0 left-0 w-full h-full pointer-events-none outline outline-[20000px] outline-white dark:outline-slate-950" />
            </div>
          )}
        </div>
      </div>
    </CameraContext.Provider>
  );
};
